import { ErrorMessage, Form, Formik } from "formik";
import React, { useEffect } from "react";
import SidebarField from "../../../../Components/SidebarField";
import Button from "../../../../Components/Button";
import TextInput from "../../../../Components/TextInput";
import SelectDropDown from "../../../../Components/Dropdown/SelectDropdown";
import { FormField } from "../../../../Components/FormField";
import { MultiSelect } from "primereact/multiselect";
import { ParcelCategoryApi } from "../../../../App/api/ParcelCategory.api";
import ParcelCategoryValidationSchema from "./ParcelCategoryValidationSchema";
import { toast } from "react-toastify";

function AddParcelCategory({ open, handleClose, currentItems, onSuccess }) {
  const allowedVehicleOptions = [
    { name: "Bike", _id: "bike" },
    { name: "Van", _id: "van" },
    { name: "Truck", _id: "truck" },
    { name: "Any", _id: "any" },
  ];

  const initialValues = {
    name: currentItems?.name || "",

    allowedVehicles: currentItems?.allowedVehicles || [],

    basePrice: currentItems?.pricing?.basePrice ?? 0,

    weightCharge: currentItems?.pricing?.weightCharge ?? 0,

    vehiclePerKmPrice: {
      bike: currentItems?.pricing?.vehiclePerKmPrice?.bike ?? 0,

      van: currentItems?.pricing?.vehiclePerKmPrice?.van ?? 0,

      truck: currentItems?.pricing?.vehiclePerKmPrice?.truck ?? 0,

      any: currentItems?.pricing?.vehiclePerKmPrice?.any ?? 0,
    },

    minWeight: currentItems?.minWeight ?? 0,

    maxWeight: currentItems?.maxWeight ?? 50,

    isActive: currentItems?.isActive ?? true,
  };

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      name: values.name,

      allowedVehicles: values.allowedVehicles,

      pricing: {
        basePrice: values.basePrice,

        weightCharge: values.weightCharge,

        vehiclePerKmPrice: {
          bike: values.vehiclePerKmPrice?.bike || 0,

          van: values.vehiclePerKmPrice?.van || 0,

          truck: values.vehiclePerKmPrice?.truck || 0,

          any: values.vehiclePerKmPrice?.any || 0,
        },
      },

      minWeight: values.minWeight,

      maxWeight: values.maxWeight,

      isActive: values.isActive,
    };

    try {
      await (currentItems?._id
        ? ParcelCategoryApi.updateParcelCategory(currentItems._id, payload)
        : ParcelCategoryApi.createParcelCategory(payload));

      toast.success(
        currentItems?._id
          ? "Parcel category updated successfully"
          : "Parcel category created successfully",
      );
      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save parcel category.",
      );
    } finally {
      resetForm();
    }
  };

  useEffect(() => {
    // enable reinitialize when a different category is selected
  }, [currentItems]);

  if (!open) return null;

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={ParcelCategoryValidationSchema}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form className="w-full gap-4 flex flex-col bg-transparent py-5 px-3">
            <SidebarField
              handleClose={handleClose}
              title={
                currentItems?._id
                  ? "Update Parcel Category"
                  : "Add New Parcel Category"
              }
              button1={
                <Button text="Cancel" type="button" onClick={handleClose} />
              }
              button2={
                <Button
                  text={currentItems?._id ? "Update" : "Submit"}
                  type="submit"
                />
              }
            >
              <FormField
                label="Category Name"
                tooltip={
                  "Enter the parcel category name visible to customers during booking."
                }
              >
                <TextInput
                  name="name"
                  type="text"
                  placeholder="Enter parcel category name"
                  value={values.name}
                  onChange={handleChange}
                />
                <ErrorMessage name="name" component="p" className="error" />
              </FormField>

              {/* <FormField
                label="Description"
                tooltip={
                  "Provide a brief description of the parcel category to help customers understand its purpose."
                }
              >
                <TextInput
                  name="description"
                  type="text"
                  placeholder="Enter description"
                  value={values.description}
                  onChange={handleChange}
                />
                <ErrorMessage
                  name="description"
                  component="p"
                  className="error"
                />
              </FormField> */}

              <FormField
                label="Allowed Vehicles"
                tooltip={
                  "Select the vehicles that are allowed to handle this parcel category."
                }
              >
                <MultiSelect
                  value={values.allowedVehicles}
                  options={allowedVehicleOptions}
                  onChange={(e) => setFieldValue("allowedVehicles", e.value)}
                  optionLabel="name"
                  optionValue="_id"
                  placeholder="Select allowed vehicles"
                  className="w-full"
                />
                <ErrorMessage
                  name="allowedVehicles"
                  component="p"
                  className="error"
                />
              </FormField>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* BASE PRICE */}
                <FormField
                  label="Base Price"
                  tooltip={
                    "Fixed starting delivery charge applied before distance calculation."
                  }
                >
                  <TextInput
                    name="basePrice"
                    type="number"
                    placeholder="Enter base price"
                    value={values.basePrice}
                    onChange={handleChange}
                  />

                  <ErrorMessage
                    name="basePrice"
                    component="p"
                    className="error"
                  />
                </FormField>

                {/* WEIGHT CHARGE */}
                <FormField
                  label="Weight Charge"
                  tooltip={
                    "Extra charge applied per weight unit when the parcel exceeds the minimum weight limit."
                  }
                >
                  <TextInput
                    name="weightCharge"
                    type="number"
                    placeholder="Enter weight charge"
                    value={values.weightCharge}
                    onChange={handleChange}
                  />

                  <ErrorMessage
                    name="weightCharge"
                    component="p"
                    className="error"
                  />
                </FormField>

                {/* BIKE PRICE */}
                {values.allowedVehicles.includes("bike") && (
                  <FormField
                    label="Bike Per Km Price"
                    tooltip={
                      "Delivery charge per kilometer for bike deliveries."
                    }
                  >
                    <TextInput
                      name="vehiclePerKmPrice.bike"
                      type="number"
                      placeholder="Enter bike per km price"
                      value={values.vehiclePerKmPrice?.bike}
                      onChange={handleChange}
                    />

                    <ErrorMessage
                      name="vehiclePerKmPrice.bike"
                      component="p"
                      className="error"
                    />
                  </FormField>
                )}

                {/* VAN PRICE */}
                {values.allowedVehicles.includes("van") && (
                  <FormField
                    label="Van Per Km Price"
                    tooltip={
                      "Delivery charge per kilometer for van deliveries."
                    }
                  >
                    <TextInput
                      name="vehiclePerKmPrice.van"
                      type="number"
                      placeholder="Enter van per km price"
                      value={values.vehiclePerKmPrice?.van}
                      onChange={handleChange}
                    />

                    <ErrorMessage
                      name="vehiclePerKmPrice.van"
                      component="p"
                      className="error"
                    />
                  </FormField>
                )}

                {/* TRUCK PRICE */}
                {values.allowedVehicles.includes("truck") && (
                  <FormField
                    label="Truck Per Km Price"
                    tooltip={
                      "Delivery charge per kilometer for truck deliveries."
                    }
                  >
                    <TextInput
                      name="vehiclePerKmPrice.truck"
                      type="number"
                      placeholder="Enter truck per km price"
                      value={values.vehiclePerKmPrice?.truck}
                      onChange={handleChange}
                    />

                    <ErrorMessage
                      name="vehiclePerKmPrice.truck"
                      component="p"
                      className="error"
                    />
                  </FormField>
                )}

                {/* ANY PRICE */}
                {values.allowedVehicles.includes("any") && (
                  <FormField
                    label="Any Vehicle Per Km Price"
                    tooltip={
                      "Default delivery charge per kilometer for any vehicle type."
                    }
                  >
                    <TextInput
                      name="vehiclePerKmPrice.any"
                      type="number"
                      placeholder="Enter any vehicle per km price"
                      value={values.vehiclePerKmPrice?.any}
                      onChange={handleChange}
                    />

                    <ErrorMessage
                      name="vehiclePerKmPrice.any"
                      component="p"
                      className="error"
                    />
                  </FormField>
                )}

                {/* MIN WEIGHT */}
                <FormField
                  label="Minimum Weight (g)"
                  tooltip={
                    "Minimum weight limit in grams for parcels in this category."
                  }
                >
                  <TextInput
                    name="minWeight"
                    type="number"
                    placeholder="Enter minimum weight"
                    value={values.minWeight}
                    onChange={handleChange}
                  />

                  <ErrorMessage
                    name="minWeight"
                    component="p"
                    className="error"
                  />
                </FormField>

                {/* MAX WEIGHT */}
                <FormField
                  label="Maximum Weight (g)"
                  tooltip={
                    "Maximum weight limit in grams for parcels in this category."
                  }
                >
                  <TextInput
                    name="maxWeight"
                    type="number"
                    placeholder="Enter maximum weight"
                    value={values.maxWeight}
                    onChange={handleChange}
                  />

                  <ErrorMessage
                    name="maxWeight"
                    component="p"
                    className="error"
                  />
                </FormField>
              </div>

              <FormField
                label="Status"
                tooltip={
                  "Enable or disable this parcel category for customer bookings."
                }
              >
                <div
                  onClick={() => setFieldValue("isActive", !values.isActive)}
                  style={{
                    width: "60px",
                    height: "30px",
                    borderRadius: "30px",
                    background: values.isActive ? "#22c55e" : "#d1d5db",
                    position: "relative",
                    cursor: "pointer",
                    transition: "0.3s",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: "3px",
                      left: values.isActive ? "33px" : "3px",
                      transition: "0.3s",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>

                <p
                  style={{
                    marginTop: "5px",
                    fontSize: "13px",
                    color: values.isActive ? "#22c55e" : "#ef4444",
                    fontWeight: 500,
                  }}
                >
                  {values.isActive ? "Active" : "Inactive"}
                </p>

                <ErrorMessage name="isActive" component="p" className="error" />
              </FormField>
            </SidebarField>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default AddParcelCategory;
